import { SketchChamferType } from './SketchCornerDrawingTypes';
import {
  SketchPointBoxSelectionRole,
  type SketchPointObject,
} from '../../mesh/sketch';
import {SketchCornerOperationContext} from './SketchCornerOperationContext';
import type {
  SketchCornerAnalysis,
  SketchCornerOperationOptions,
  SketchCornerOperationResult,
} from './SketchCornerTypes';
import {SKETCH_GEOMETRY_TOLERANCE} from './SketchCornerTypes';
import {SketchChamferGeometryResolver} from './SketchChamferGeometryResolver';

/**
 * 草图倒角执行服务。
 * 职责：
 * 1. 校验倒角参数与可修剪范围；
 * 2. 计算倒角连接点并创建倒角线；
 * 3. 完成旧角点替换与清理。
 */
export class SketchChamferOperationService {
  /** 倒角上下文。 */
  private readonly context: SketchCornerOperationContext;
  /** 倒角纯几何参数解析器。 */
  private readonly geometryResolver: SketchChamferGeometryResolverLike;

  /**
   * 构造函数。
   * @param context 倒角上下文。
   * @param geometryResolver 倒角纯几何参数解析器。
   */
  public constructor(
    context: SketchCornerOperationContext,
    geometryResolver: SketchChamferGeometryResolverLike = new SketchChamferGeometryResolver(),
  ) {
    this.context = context;
    this.geometryResolver = geometryResolver;
  }

  /**
   * 执行草图倒角。
   * @param cornerAnalysis 角点解析结果。
   * @param angle 两线夹角。
   * @param options 操作参数。
   * @param isConstruction 是否构造线。
   * @returns 执行结果。
   */
  public execute(
    cornerAnalysis: SketchCornerAnalysis,
    angle: number,
    options: SketchCornerOperationOptions,
    isConstruction: boolean,
  ): SketchCornerOperationResult {
    const chamferType = this.context.normalizeChamferType(options.chamferType);
    const distance1 = Number(options.distance1 ?? 10);
    const angleDeg = Number(options.angleDeg ?? 45);
    let distance2 = Number(options.distance2 ?? distance1);

    if (!Number.isFinite(distance1) || distance1 <= SKETCH_GEOMETRY_TOLERANCE) {
      return {success: false, message: '倒角距离必须大于 0'};
    }

    if (chamferType === SketchChamferType.EqualDistance) {
      distance2 = distance1;
    } else if (chamferType === SketchChamferType.AngleDistance) {
      const resolvedDistance2 = this.geometryResolver.resolveSecondTrimDistance(
        angle,
        distance1,
        angleDeg,
      );
      if (resolvedDistance2 === null) {
        return {success: false, message: '倒角角度无效'};
      }
      distance2 = resolvedDistance2;
    }

    if (
      !Number.isFinite(distance2)
      || distance2 <= SKETCH_GEOMETRY_TOLERANCE
    ) {
      return {success: false, message: '倒角距离必须大于 0'};
    }
    if (
      !this.context.isTrimDistanceWithinLineRange(distance1, cornerAnalysis.firstUsage)
      || !this.context.isTrimDistanceWithinLineRange(distance2, cornerAnalysis.secondUsage)
    ) {
      return {success: false, message: '倒角距离过大，超出线段可修剪范围'};
    }

    const chamferPoint1Local = cornerAnalysis.cornerLocal.clone().addScaledVector(cornerAnalysis.firstUsage.directionLocal, distance1);
    const chamferPoint2Local = cornerAnalysis.cornerLocal.clone().addScaledVector(cornerAnalysis.secondUsage.directionLocal, distance2);
    const referencePlane = cornerAnalysis.firstUsage.lineData.line.referencePlane;
    const chamferPoint1World = referencePlane.localToWorld(chamferPoint1Local);
    const chamferPoint2World = referencePlane.localToWorld(chamferPoint2Local);

    const chamferStartPoint = this.context.createCornerControlPoint(
      chamferPoint1World,
      undefined,
      referencePlane,
    );
    const chamferFinishPoint = this.context.createCornerControlPoint(
      chamferPoint2World,
      undefined,
      referencePlane,
    );
    const firstOldCornerPoint = cornerAnalysis.firstUsage.cornerPoint;
    const secondOldCornerPoint = cornerAnalysis.secondUsage.cornerPoint;
    this.context.replaceLineCornerControlPoint(
      cornerAnalysis.firstUsage.lineData.line,
      cornerAnalysis.firstUsage.cornerRole,
      chamferStartPoint,
    );
    this.context.replaceLineCornerControlPoint(
      cornerAnalysis.secondUsage.lineData.line,
      cornerAnalysis.secondUsage.cornerRole,
      chamferFinishPoint,
    );
    this.context.removeDetachedPoint(firstOldCornerPoint);
    this.context.removeDetachedPoint(secondOldCornerPoint);

    const {line: chamferLine, controlPoints: chamferControlPoints} = this.context.createSketchLineEntity(
      chamferPoint1World,
      chamferPoint2World,
      isConstruction,
      chamferStartPoint,
      chamferFinishPoint,
      referencePlane,
    );

    //------ 倒角创建后关系收口 开始------
    /**
     * 倒角创建后的关系策略：
     * 1. 所有倒角模式统一创建线延长线交点控制点，并把该点约束到两条源线的真实交点；
     * 2. 这样后续无论用户如何拖拽倒角线或源线，都有稳定的“原始角点焦点”可参与求解；
     * 3. 仅在“角度-距离”倒角模式下，额外挂载“第一条线 + 倒角线”的角度约束，锁定夹角不变；
     * 4. 原交点焦点具有独立选择语义，因此仅给该点写入专用框选角色；
     *    两个倒角端点仍是普通拓扑控制点，不扩大框选范围；
     * 5. 统一把交点控制点返回给调用方，确保上层能完整接管新创建的控制点集合。
     */
    const intersectionFocusPoint: SketchPointObject = this.context.createCornerControlPoint(
      cornerAnalysis.cornerWorld.clone(),
      SketchPointBoxSelectionRole.ChamferIntersectionFocus,
      referencePlane,
    );
    this.context.bindLineIntersectionRelation(
      cornerAnalysis.firstUsage.lineData.line,
      cornerAnalysis.secondUsage.lineData.line,
      intersectionFocusPoint,
    );
    if (chamferType === SketchChamferType.AngleDistance) {
      this.context.bindLineAngleRelation(
        cornerAnalysis.firstUsage.lineData.line,
        chamferLine,
      );
    }
    /**
     * 距离类倒角的两个缩进量不再挂匿名距离关系：SW 的倒角默认带**两条距离尺寸**，
     * 距离锁由自动 driving 尺寸直接编译的参数方程提供
     * （由上层命令控制器在同一笔事务里创建），关系面板只投影只读快照。
     */
    //------ 倒角创建后关系收口 结束------

    return {
      success: true,
      message: '草图倒角已创建',
      createdObject: chamferLine,
      controlPoints: [...chamferControlPoints, intersectionFocusPoint],
    };
  }
}
